import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoice, faDownload, faArrowUpRightFromSquare,
  faCircleExclamation, faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import "./BillingInvoices.css";

const BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * Downloadable billing documents for one agent.
 *
 * Self-gating by design: the backend merges subscription invoices with
 * message-credit receipts and returns an empty list for an agent that has
 * neither, so this renders `null` rather than an empty card. That keeps the
 * mount site in BotBilling free of rail logic — a Wix-billed agent that bought
 * credits through Stripe still gets its receipts here.
 */
function BillingInvoices({ botId, onOpenPortal }) {
  const { t, i18n } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!botId) return;
    try {
      const res = await fetch(`${BASE}bots/${botId}/invoices`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setDocs(Array.isArray(json.documents) ? json.documents : []);
      setError(null);
    } catch (e) {
      setError(t("billing_invoices_error"));
    } finally {
      setLoading(false);
    }
  }, [botId, t]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Dates are stored naive-UTC; pin the Z so the browser doesn't read them as
  // local time and shift the day across a timezone boundary.
  const fmtDate = (iso) => {
    if (!iso) return "";
    const stamp = /[Z+]|-\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
    const d = new Date(stamp);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(i18n.language, {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const fmtAmount = (amount, currency) => {
    try {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency", currency: currency || "USD",
      }).format(amount || 0);
    } catch {
      return `${(amount || 0).toFixed(2)} ${currency || ""}`.trim();
    }
  };

  // Nothing paid yet (Free agent, or a platform-billed one with no top-ups):
  // stay invisible instead of showing an empty table.
  if (!loading && !error && docs.length === 0) return null;

  return (
    <section className="bbi-invoices">
      <div className="bbi-head">
        <h2 className="bbi-title">
          <FontAwesomeIcon icon={faFileInvoice} /> {t("billing_invoices_title")}
        </h2>
        <p className="bbi-sub">{t("billing_invoices_sub")}</p>
      </div>

      {error && (
        <div className="bbi-error">
          <FontAwesomeIcon icon={faCircleExclamation} /> {error}
        </div>
      )}

      {loading && (
        <div className="bbi-loading">
          <FontAwesomeIcon icon={faSpinner} spin /> {t("billing_invoices_loading")}
        </div>
      )}

      {!loading && docs.length > 0 && (
        <div className="bbi-table-wrap">
          <table className="bbi-table">
            <thead>
              <tr>
                <th>{t("billing_invoices_col_date")}</th>
                <th>{t("billing_invoices_col_desc")}</th>
                <th className="bbi-num">{t("billing_invoices_col_amount")}</th>
                <th className="bbi-act" aria-label={t("billing_invoices_col_document")} />
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td className="bbi-date">{fmtDate(d.date)}</td>
                  <td className="bbi-desc">{d.label}</td>
                  <td className="bbi-num">{fmtAmount(d.amount, d.currency)}</td>
                  <td className="bbi-act">
                    {d.pdf_url ? (
                      <a
                        className="bbi-link"
                        href={d.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon={faDownload} /> {t("billing_invoices_pdf")}
                      </a>
                    ) : d.view_url ? (
                      <a
                        className="bbi-link bbi-link-soft"
                        href={d.view_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />{" "}
                        {t("billing_invoices_view")}
                      </a>
                    ) : (
                      <span className="bbi-none">{t("billing_invoices_unavailable")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && onOpenPortal && (
        <button type="button" className="bbi-all" onClick={onOpenPortal}>
          {t("billing_invoices_all_in_stripe")}{" "}
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </button>
      )}
    </section>
  );
}

export default BillingInvoices;
